import Config from "../../../core/config/Config";
import NGTConfigKey from "../../../ngt/base/NGTConfigKey";
import NGTVariableManager from "../../../ngt/base/NGTVariableManager";
import BackendService from "../../backend/controller/BackendService";
import GameConversationMessage from "../../conversation/model/GameConversationMessage";
import DebugModel from "../../debug/model/DebugModel";
import PlayerModel from "../../player/model/PlayerModel";
import ConversationReflectionAllowEndEvent from "../event/ConversationReflectionAllowEndEvent";
import ConversationReflectionEndedEvent from "../event/ConversationReflectionEndedEvent";
import ConversationReflectionStartedEvent from "../event/ConversationReflectionStartedEvent";
import NewConversationReflectionMessagesEvent from "../event/NewConversationReflectionMessagesEvent";
import ConversationReflection from "../model/ConversationReflection";
import ConversationReflectionState from "./state/ConversationReflectionState";
import StartingTurnConversationReflectionState from "./state/StartingTurnConversationReflectionState";

export default class ConversationReflectionPlayer {
    /**
     * @constructor
     * @param {soma.EventDispatcher} dispatcher 
     * @param {BackendService} backendService 
     * @param {Config} config 
     * @param {PlayerModel} playerModel 
     * @param {NGTVariableManager} variableManager 
     */
    constructor(dispatcher, backendService, config, playerModel, variableManager) {
        this._dispatcher = dispatcher;
        this._backendService = backendService;
        this._config = config;
        this._playerModel = playerModel;
        this._variableManager = variableManager;

        /** @type {ConversationReflection} */
        this._conversation = null;
        /** @type {ConversationReflectionState} */
        this._currentState = null;

        /** @type {object} */
        this._conversationSnapshotData = null;
        /** @type {ConversationReflectionState} */
        this._conversationSnapshotState = null;
    }

    destroy() {
        this._conversation?.removeAllEventListeners();
    }

    /**
     * @param {string} chapterId 
     * @param {string} conversationId 
     */
    async start(chapterId, conversationId) {
        const maximumPlayerTurns = DebugModel.maxReflectionTurns || this._config.getValue(NGTConfigKey.MAXIMUM_REFLECTION_TURNS);
        this._conversation = new ConversationReflection(conversationId, chapterId, maximumPlayerTurns);
        this._currentState = new StartingTurnConversationReflectionState();

        this._saveConversationSnapshot(); // Save so that we can roll-back to the beginning aswell if needed (e.g. due content filter violation).

        this._addConversationListeners();

        const conversationStartedEvent = new ConversationReflectionStartedEvent(ConversationReflectionStartedEvent.PERFORM, chapterId, conversationId);
        this._dispatcher.dispatch(conversationStartedEvent.type, { data: conversationStartedEvent });
    }

    async next() {
        console.log(`[${ConversationReflectionPlayer.name}] Executing state: ${this._currentState?.constructor.name}`);

        let nextState = null;
        try {
            nextState = await this._currentState.execute(this._conversation.chapterId, this._conversation, this._backendService, this._playerModel);
        } catch (ex) {
            console.error(`[${ConversationReflectionPlayer.name}] Error executing state. Rolling back to conversation snapshot.`, ex);

            this._currentState.destroy();

            // Load the conversation snapshot.
            this._conversation.load(this._conversationSnapshotData);
            this._currentState = this._conversationSnapshotState;
            this._currentState.reset();

            // Continue playing.
            return await this.next();
        }

        if (nextState && nextState != this._currentState) {
            this._addEventListenersToState(nextState);

            // const conversationReflectionStateChangedEvent = new ConversationReflectionStateChangedEvent(this._conversation, nextState);
            // this._dispatcher.dispatch(conversationReflectionStateChangedEvent.type, { data: conversationReflectionStateChangedEvent });
        }

        this._currentState.destroy();

        if (this._conversation.hasEnded) {
            this.end();
            return;
        }

        const minimumPlayerTurns = DebugModel.minReflectionTurns || this._config.getValue(NGTConfigKey.MINIMUM_REFLECTION_TURNS);
        if (this._conversation.turns === minimumPlayerTurns) {
            const conversationReflectionAllowEndEvent = new ConversationReflectionAllowEndEvent(ConversationReflectionAllowEndEvent.PERFORM, this._conversation);
            this._dispatcher.dispatch(conversationReflectionAllowEndEvent.type, { data: conversationReflectionAllowEndEvent });
        }

        console.log(`[${ConversationReflectionPlayer.name}] Next state: ${nextState?.constructor.name}`);
        this._currentState = nextState;

        return await this.next();
    }

    end() {
        console.log(`[${ConversationReflectionPlayer.name}] Reflection conversation has ended.`);

        const conversationEndedEvent = new ConversationReflectionEndedEvent(ConversationReflectionEndedEvent.PERFORM, this._conversation);
        this._dispatcher.dispatch(conversationEndedEvent.type, { data: conversationEndedEvent });
    }

    /**
     * 
     * @param {string} message 
     */
    submitPlayerInput(message) {
        this._currentState?.handlePlayerInput(message);
    }

    _addConversationListeners() {
        this._conversation.on('messages', (evt) => {
            /** @type {GameConversationMessage[]} */
            const gameConversationMessages = evt.gameConversationMessages;
            const newConversationMessagesEvent = new NewConversationReflectionMessagesEvent(this._conversation, gameConversationMessages);
            this._dispatcher.dispatch(newConversationMessagesEvent.type, { data: newConversationMessagesEvent });
        });
    }

    _addEventListenersToState(state) {
        state.on(ConversationReflectionState.SAVE_SNAPSHOT, this._saveConversationSnapshot.bind(this));
    }

    _saveConversationSnapshot() {
        if (this._conversation === null || this._currentState === null) {
            console.error(`[${ConversationReflectionPlayer.name}] Cannot save conversation snapshot. Conversation or state is null.`);
            return;
        }

        this._conversationSnapshotData = this._conversation.toObject();
        this._conversationSnapshotState = this._currentState;
    }

    get conversation() {
        return this._conversation;
    }
}